import {
  Search,
  Grid3X3,
  BookOpen,
  Globe,
  Camera,
  Satellite,
  Radar,
  Footprints,
  Rocket,
  PencilRuler,
  ClipboardCheck,
  Share2,
  Target,
  Smartphone,
  Layers,
  Building2,
  MapPin,
  MapPinned,
  Filter,
  Star,
  Shield,
  Sparkles,
  UserCog,
  Trophy,
  type LucideIcon,
} from "lucide-react";

// Real product screenshots, captured from the live app (see ./assets).
import step1Img from "./assets/step-1.png";
import step2Img from "./assets/step-2.png";
import step3Img from "./assets/step-3.png";
import step4Img from "./assets/step-4.png";
import step5Img from "./assets/step-5.png";
import step6Img from "./assets/step-6.png";
import step7Img from "./assets/step-7.png";
import step8Img from "./assets/step-8.png";
import step9Img from "./assets/step-9.png";

/**
 * Single source of truth for the "How it works" page content.
 *
 * Drives both the in-app (`/dashboard/how-it-works`) and public
 * (`/how-it-works`) renders, the hero overview, the two-phase timeline,
 * the nested AI scan-pipeline deep-dive, and the impact-metrics grid.
 *
 * The scan pipeline data here reflects the ACTUAL implementation in
 * `src/server/scanner/pipeline.ts` (PHASE_ORDER) — keep it in sync if the
 * scanner changes.
 */

/* ------------------------------------------------------------------ */
/* Shared visual tokens                                               */
/* ------------------------------------------------------------------ */

export interface PhaseColor {
  /** left-border accent, e.g. "border-blue-500" */
  border: string;
  /** icon chip background, e.g. "bg-blue-100" */
  bg: string;
  /** icon + label text, e.g. "text-blue-600" */
  text: string;
}

/* ------------------------------------------------------------------ */
/* High-level phases (hero + section banners)                         */
/* ------------------------------------------------------------------ */

export type FlowPhase = "digital" | "physical" | "export";

export interface PhaseMeta {
  id: FlowPhase;
  label: string;
  tagline: string;
  icon: LucideIcon;
  color: PhaseColor;
  /** step numbers that belong to this phase, in order */
  stepNumbers: number[];
}

export const PHASE_META: Record<FlowPhase, PhaseMeta> = {
  digital: {
    id: "digital",
    label: "Digital Mapping",
    tagline: "Discover · research · review · share",
    icon: Radar,
    color: { border: "border-blue-500", bg: "bg-blue-100", text: "text-blue-600" },
    stepNumbers: [1, 2, 3, 4],
  },
  physical: {
    id: "physical",
    label: "Physical Mapping",
    tagline: "On-site survey · tier · city validation",
    icon: Footprints,
    color: { border: "border-amber-500", bg: "bg-amber-100", text: "text-amber-600" },
    stepNumbers: [5, 6, 7, 8],
  },
  export: {
    id: "export",
    label: "Export",
    tagline: "Deliver the validated list to Equip Dashboard",
    icon: Rocket,
    color: { border: "border-emerald-500", bg: "bg-emerald-100", text: "text-emerald-600" },
    stepNumbers: [9],
  },
};

export const PHASE_ORDER: FlowPhase[] = ["digital", "physical", "export"];

/* ------------------------------------------------------------------ */
/* Roles / actors                                                     */
/* ------------------------------------------------------------------ */

export type FlowRole = "ai" | "admin" | "mapper" | "city";

export interface RoleMeta {
  label: string;
  icon: LucideIcon;
  /** chip background + text classes */
  bg: string;
  text: string;
}

export const ROLE_META: Record<FlowRole, RoleMeta> = {
  ai: { label: "Equip Scout AI", icon: Sparkles, bg: "bg-violet-100", text: "text-violet-700" },
  admin: { label: "Equip Admin", icon: UserCog, bg: "bg-slate-100", text: "text-slate-700" },
  mapper: { label: "Physical Mapper", icon: Footprints, bg: "bg-amber-100", text: "text-amber-700" },
  city: { label: "City", icon: Building2, bg: "bg-sky-100", text: "text-sky-700" },
};

/* ------------------------------------------------------------------ */
/* The 9 end-to-end steps                                             */
/* ------------------------------------------------------------------ */

export interface FlowStep {
  number: number;
  phase: FlowPhase;
  title: string;
  icon: LucideIcon;
  /** one-line summary shown when the step card is collapsed */
  summary: string;
  /** business narrative — what happens at this step */
  whatHappens: string;
  /** technical bullets — routes, APIs, models, tables */
  howItWorks: string[];
  /** who acts (left → right shows handoffs) */
  roles: FlowRole[];
  /** artifacts / DB tables produced */
  dataProduced: string[];
  /** primary route shown as a chip */
  route?: string;
  /** caption for the screenshot placeholder slot */
  screenshotCaption: string;
  /** real product screenshot (imported asset). Falls back to the placeholder when absent. */
  screenshot?: string;
  /** optional "what happens between steps" note rendered under the card */
  betweenNote?: string;
  /** marks the step that owns the nested AI scan-pipeline deep-dive */
  hasDeepDive?: boolean;
  /** visually emphasize this step as the standout "magic" moment */
  highlight?: boolean;
}

export const FLOW_STEPS: FlowStep[] = [
  /* ── DIGITAL MAPPING ─────────────────────────────────────────── */
  {
    number: 1,
    phase: "digital",
    title: "Scan request",
    summary: "An admin draws the target area and picks which sports to find.",
    icon: PencilRuler,
    whatHappens:
      "An Equip admin draws the target area on a map and configures the scan — which sport types to look for, the language/region, and which categories to auto-exclude (private, institutional, no field). This kicks everything off.",
    howItWorks: [
      "Polygon drawn on an interactive Google Map; city boundaries resolved via Nominatim (scanRequests.searchCity).",
      "scanRequests.create persists the GeoJSON polygon, requested sport types and config to the scan_requests table.",
      "Status workflow pending → approved → running → completed; approving a request triggers the scan pipeline (scanRequests.runRequest).",
    ],
    roles: ["admin"],
    dataProduced: ["scan_requests (polygon, requested_variables, status)"],
    route: "/dashboard/scan-requests",
    screenshotCaption: "Scan request — polygon-based requests across cities, with sport types and status",
    screenshot: step1Img,
  },
  {
    number: 2,
    phase: "digital",
    title: "Fully automated AI scan",
    summary: "AI sweeps the area and builds a scored shortlist of candidate locations.",
    icon: Radar,
    whatHappens:
      "Equip Scout autonomously sweeps the area and builds a curated shortlist of every candidate sport location — discovering them, capturing satellite imagery, cross-checking the open web, and scoring each one.",
    howItWorks: [
      "A six-stage AI pipeline runs server-side (see deep dive below): Google Places discovery → Claude web/website/satellite analysis → auto-rejection.",
      "Per-stage Claude models are configurable (defaults: Claude Sonnet 5.5 for research & satellite, Claude Haiku 5.5 for website).",
      "Writes locations, classifications, screenshots and sport_courts; weak candidates are auto-rejected with cross-source protection.",
    ],
    roles: ["ai"],
    dataProduced: ["locations", "classifications", "screenshots", "sport_courts"],
    screenshotCaption: "The curated candidate set — every discovered location plotted, classified and scored",
    screenshot: step2Img,
    betweenNote:
      "The output is a curated candidate list — not a finished deliverable. Nothing leaves the system until a human reviews it.",
    hasDeepDive: true,
    highlight: true,
  },
  {
    number: 3,
    phase: "digital",
    title: "Review",
    summary: "A human confirms the real facilities and discards false positives.",
    icon: ClipboardCheck,
    whatHappens:
      "An admin curates the candidate list one location at a time — confirming real sport facilities and rejecting false positives, using the AI's satellite screenshots and confidence score as a guide. This is the human checkpoint before the city ever sees the list.",
    howItWorks: [
      "Review queue (locations.reviewQueue) pairs each candidate's satellite shots with its AI classification.",
      "locations.updateClassification / locations.reject set review_status (unreviewed → reviewed / rejected).",
      "Reviewed locations form the shareable collection (collection_locations).",
    ],
    roles: ["admin"],
    dataProduced: ["locations.review_status", "collection_locations"],
    route: "/dashboard/review",
    screenshotCaption: "Review queue — confirming or rejecting each AI-surfaced candidate",
    screenshot: step3Img,
  },
  {
    number: 4,
    phase: "digital",
    title: "Share → 1st city review",
    summary: "The reviewed list goes to the city for a first round of feedback.",
    icon: Share2,
    whatHappens:
      "The reviewed list is shared with the city through a public link — an interactive map they can explore, filter and export. The city sends back feedback, and the admin manually adjusts the reviewed list accordingly. This closes Digital Mapping.",
    howItWorks: [
      "Public route /share/<token> (no login) backed by public tRPC procedures (share.getCollection / getMapData / listLocations / exportCsv|Xlsx|Kml).",
      "Each collection carries a unique share_token; the city sees map pins, ownership badges, filters and data exports.",
      "The admin incorporates city feedback by editing the reviewed collection directly.",
    ],
    roles: ["admin", "city"],
    dataProduced: ["collections.share_token", "city feedback (applied manually)"],
    route: "/share/<token>",
    screenshotCaption: "Public share link — the city explores the reviewed locations on a map",
    screenshot: step4Img,
    betweenNote:
      "End of Digital Mapping. The agreed list now moves on-site for Physical Mapping.",
  },

  /* ── PHYSICAL MAPPING ────────────────────────────────────────── */
  {
    number: 5,
    phase: "physical",
    title: "Minimum-spots setup",
    summary: "Admin sets how many installation spots each location needs.",
    icon: Target,
    whatHappens:
      "Before sending anyone on-site, an admin works through the list in the Physical Mapping overview, setting the minimum number of installation spots each location needs and seeding candidate spots for the mapper to verify.",
    howItWorks: [
      "Overview tab sets locations.min_installation_spots (1–50) per location.",
      "mapping_status (not_started → pending → in_progress → done) is derived by compute_location_status once the survey, an overview photo and the minimum placed spots are met.",
      "physicalMapping.list drives the overview table (per-location spot counts + status).",
    ],
    roles: ["admin"],
    dataProduced: [
      "locations.min_installation_spots",
      "locations.mapping_status",
      "seed potential_installation_locations",
    ],
    route: "/dashboard/physical-mapping",
    screenshotCaption: "Physical Mapping overview — per-location status and installation-spot counts",
    screenshot: step5Img,
  },
  {
    number: 6,
    phase: "physical",
    title: "On-site mapper form",
    summary: "A mapper visits each site and records conditions, photos and spots.",
    icon: Smartphone,
    whatHappens:
      "A physical mapper visits each location in person and fills in a mobile-first form: a site survey (public access, hours, visibility, surroundings, safety, lighting, degradation), overview photos, and one or more installation spots — each with photos and locker measurements.",
    howItWorks: [
      "Public mobile route /mapping/<token>/location/<id> (token-based, no login).",
      "share.getMappingLocation loads the location; share.createMapperPIL places spots; the survey saves via setLocationMappingFields; photos upload to the pil-photos bucket.",
      "Captured to potential_installation_locations, pil_photos, pil_lockers, location_mapping_photos and the mapping_* columns on locations.",
    ],
    roles: ["mapper"],
    dataProduced: [
      "potential_installation_locations",
      "pil_photos",
      "pil_lockers",
      "location_mapping_photos",
      "locations.mapping_* fields",
    ],
    route: "/mapping/<token>/location/<id>",
    screenshotCaption: "Mobile mapper form — on-site survey, photos and installation-spot placement",
    screenshot: step6Img,
  },
  {
    number: 7,
    phase: "physical",
    title: "Admin tier review",
    summary: "Admin scores each spot Tier 1–3 using the on-site data.",
    icon: Layers,
    whatHappens:
      "With the mapper's real-world data in hand (photos, access, degradation, lockers), an admin re-reviews every spot and assigns a Tier — 1, 2 or 3 — reflecting how likely we are to actually use it, approving or rejecting each one.",
    howItWorks: [
      "Spot Review tab: physicalMapping.setAdminTier writes admin_tier (1–3); approvePIL / rejectPIL move a spot to admin_approved or rejected.",
      "Admins can edit locker details and add admin_notes; status changes recompute the location's mapping status.",
    ],
    roles: ["admin"],
    dataProduced: [
      "potential_installation_locations.admin_tier",
      "status = admin_approved",
      "admin_notes",
    ],
    route: "/dashboard/physical-mapping?tab=review",
    screenshotCaption: "Spot review — the mapper's satellite + on-site photos behind each tier decision",
    screenshot: step7Img,
  },
  {
    number: 8,
    phase: "physical",
    title: "City review (Main / Backup)",
    summary: "The city validates each Main/Backup station and leaves comments.",
    icon: Building2,
    whatHappens:
      "Approved spots flow into the City Review tab, where the admin labels each as a Main or Backup station and sends the city a public link. The city approves, rejects or requests changes on each station and leaves comments.",
    howItWorks: [
      "Admin (City Review tab): physicalMapping.setCityVerdictByAdmin sets role (main / backup) against the collection's hub_size target.",
      "Public /city-review/<token> (report_token): cityReport.getReport renders the proposed stations; cityReport.submitCityFeedback records city_verdict (approve / reject / needs_change) and pil_city_feedback comments.",
    ],
    roles: ["admin", "city"],
    dataProduced: [
      "potential_installation_locations.role",
      "city_verdict",
      "pil_city_feedback",
      "collections.hub_size",
    ],
    route: "/city-review/<token>",
    screenshotCaption: "City review — proposed stations with Main/Backup designation and city verdicts",
    screenshot: step8Img,
  },

  /* ── EXPORT ──────────────────────────────────────────────────── */
  {
    number: 9,
    phase: "export",
    title: "Export → Equip Dashboard",
    summary: "The fully-validated list is pushed live to the Equip Dashboard.",
    icon: Rocket,
    whatHappens:
      "Once a station is validated by all three parties — Equip admin, physical mapper and the city — the final list is exported into the Equip Dashboard, the separate app that runs the live sport stations.",
    howItWorks: [
      "Push-to-prod runs a 13-step idempotent sync (src/server/sync/push-to-prod.ts): scan_configs → scans → scan_cells → locations → screenshots → classifications → sport_courts → PILs → pil_photos → collection → backfill.",
      "Writes to the production Equip Dashboard via getProdSupabaseAdmin(); collection_locations.pushed_to_prod marks what has already been delivered.",
    ],
    roles: ["admin", "ai"],
    dataProduced: ["Equip Dashboard records", "collection_locations.pushed_to_prod = true"],
    screenshotCaption: "Equip Dashboard — validated stations land in the live app, ready to go operational",
    screenshot: step9Img,
  },
];

/** The three parties that must all sign off before export (closing callout). */
export const VALIDATION_PARTIES: FlowRole[] = ["admin", "mapper", "city"];

/* ------------------------------------------------------------------ */
/* AI scan pipeline deep-dive (under step 2)                          */
/* Order mirrors PHASE_ORDER in src/server/scanner/pipeline.ts        */
/* ------------------------------------------------------------------ */

export interface PipelineStageRejection {
  text: string;
  /** skipped when another source confirmed a sport field */
  protected?: boolean;
}

export interface PipelineStage {
  stage: number;
  title: string;
  description: string;
  icon: LucideIcon;
  color: PhaseColor;
  badges: string[];
}

export const PIPELINE_STAGES: PipelineStage[] = [
  {
    stage: 1,
    title: "Keyword Scanning",
    description:
      "Generates search keywords from the configured sport types and translates them to the target language, then runs Google Places Text Search across grid cells covering the polygon — recursively subdividing cells that hit the result cap.",
    icon: Search,
    color: { border: "border-blue-500", bg: "bg-blue-100", text: "text-blue-600" },
    badges: ["Google Places Text Search", "Keyword generation", "Multi-language", "Grid subdivision"],
  },
  {
    stage: 2,
    title: "Nearby Search",
    description:
      "Splits the polygon into a grid and runs a Google Places Nearby Search per cell, subdividing dense cells for deeper coverage. Deduplicates within ~50m and pre-flags institutional places from Google's place types.",
    icon: Grid3X3,
    color: { border: "border-indigo-500", bg: "bg-indigo-100", text: "text-indigo-600" },
    badges: ["Google Places Nearby Search", "Grid subdivision", "~50m dedup", "Institution pre-detection"],
  },
  {
    stage: 3,
    title: "Web Research",
    description:
      "Claude searches the open web to independently validate each candidate — does a sport field exist, who owns it, what are the opening hours — and assigns a confidence score. Updates ownership when its confidence is high and beats existing sources.",
    icon: BookOpen,
    color: { border: "border-emerald-500", bg: "bg-emerald-100", text: "text-emerald-600" },
    badges: ["Claude Sonnet 5.5", "WebSearch", "Multi-source validation", "Opening hours"],
  },
  {
    stage: 4,
    title: "Website Classification",
    description:
      "For candidates with a website, Claude fetches the site to classify ownership (public / private / institutional / unknown). If the site is unreachable it falls back to a web search instead of retrying. Parks and playgrounds are always treated as public.",
    icon: Globe,
    color: { border: "border-teal-500", bg: "bg-teal-100", text: "text-teal-600" },
    badges: ["Claude Haiku 5.5", "WebFetch → WebSearch fallback", "Ownership detection"],
  },
  {
    stage: 5,
    title: "Screenshot Capture",
    description:
      "Captures two satellite images per location with Playwright + a stealth plugin — zoom 19 for field markings and zoom 18 for surrounding context — and stores them in Supabase Storage.",
    icon: Camera,
    color: { border: "border-amber-500", bg: "bg-amber-100", text: "text-amber-600" },
    badges: ["Playwright Stealth", "Dual zoom (19 + 18)", "Supabase Storage"],
  },
  {
    stage: 6,
    title: "Satellite Classification",
    description:
      "Claude analyzes both satellite images (plus any Google map-label overlays) to detect sport-field presence, identify sport types, score confidence and flag institutional sites.",
    icon: Satellite,
    color: { border: "border-purple-500", bg: "bg-purple-100", text: "text-purple-600" },
    badges: ["Claude Sonnet 5.5", "Dual image vision", "Sport detection", "Confidence score"],
  },
];

export interface AutoRejectionRule {
  text: string;
  detail: string;
  /** gated behind a scan-config toggle */
  toggle?: string;
  /** skipped if any source confirmed a sport field */
  protected?: boolean;
}

/** Runs once after all six stages, before the curated list is finalized. */
export const AUTO_REJECTIONS: AutoRejectionRule[] = [
  {
    text: "No sport field detected",
    detail: "Satellite reports no field with confidence ≥ 0.7.",
    toggle: "excludeNoSportField",
    protected: true,
  },
  {
    text: "Institutional location",
    detail: "Ownership resolved to institutional (school, university).",
    toggle: "excludeInstitutions",
  },
  {
    text: "Private facility",
    detail: "Ownership resolved to private.",
    toggle: "excludePrivate",
  },
  {
    text: "Low confidence",
    detail: "Best confidence across all sources is below 0.5.",
    protected: true,
  },
];

export const CROSS_SOURCE_NOTE =
  "Rules marked * are skipped whenever any source (web research, website or satellite) confirmed a sport field — a strong signal anywhere protects the candidate from being dropped.";

/* ------------------------------------------------------------------ */
/* Impact metrics                                                     */
/* ------------------------------------------------------------------ */

/**
 * Shared contract between the live backend procedure
 * (analytics.impactMetrics) and the static public snapshot
 * (impactMetrics.static.ts). Both MUST satisfy this shape.
 */
export interface ImpactMetricsData {
  // Discovery funnel
  totalLocations: number;
  autoRejectedCount: number;
  autoRejectionRate: number; // 0..1
  passedToReviewCount: number;
  // Coverage & scale
  collectionsCount: number;
  scansCount: number;
  distinctSportTypes: number;
  // Physical mapping output
  locationsMapped: number; // mapping_status = 'done'
  pilsPlaced: number; // potential_installation_locations count
  photosCaptured: number; // pil_photos + location_mapping_photos
  // Validation & delivery
  mainStations: number; // role = 'main'
  backupStations: number; // role = 'backup'
  cityReviewedCount: number; // city_verdict IS NOT NULL
  exportedToProd: number; // collection_locations.pushed_to_prod = true
}

export interface ImpactMetricDisplay {
  key: keyof ImpactMetricsData;
  label: string;
  icon: LucideIcon;
  /** "number" (default) renders the integer; "percent" renders a 0..1 value as % */
  kind?: "number" | "percent";
  /** optional supporting field rendered as subtext (e.g. the raw count behind a rate) */
  subtextKey?: keyof ImpactMetricsData;
  /** template for subtext; {value} is replaced with the formatted subtextKey value */
  subtextTemplate?: string;
}

export interface ImpactMetricGroup {
  id: string;
  title: string;
  blurb: string;
  metrics: ImpactMetricDisplay[];
}

export const IMPACT_METRIC_GROUPS: ImpactMetricGroup[] = [
  {
    id: "discovery",
    title: "Discovery funnel",
    blurb: "Equip Scout does the heavy lifting — surfacing candidates and filtering the noise before a human looks.",
    metrics: [
      { key: "totalLocations", label: "Potential locations discovered", icon: MapPin },
      {
        key: "autoRejectionRate",
        label: "Auto-filtered by AI",
        icon: Filter,
        kind: "percent",
        subtextKey: "autoRejectedCount",
        subtextTemplate: "{value} candidates dropped",
      },
      { key: "passedToReviewCount", label: "Passed human review", icon: ClipboardCheck },
    ],
  },
  {
    id: "coverage",
    title: "Coverage & scale",
    blurb: "Reach across cities and sports — the engine runs the same way at any scale.",
    metrics: [
      { key: "collectionsCount", label: "Cities / collections", icon: Building2 },
      { key: "scansCount", label: "Scans run", icon: Radar },
      { key: "distinctSportTypes", label: "Sport types covered", icon: Trophy },
    ],
  },
  {
    id: "physical",
    title: "Physical mapping output",
    blurb: "Real-world execution — every shortlisted location verified on the ground.",
    metrics: [
      { key: "locationsMapped", label: "Locations mapped on-site", icon: MapPinned },
      { key: "pilsPlaced", label: "Installation spots reviewed", icon: Target },
      { key: "photosCaptured", label: "Field photos captured", icon: Camera },
    ],
  },
  {
    id: "validation",
    title: "Validation & delivery",
    blurb: "The finished product — stations validated by admin, mapper and city, then shipped.",
    metrics: [
      { key: "mainStations", label: "Main stations", icon: Star },
      { key: "backupStations", label: "Backup stations", icon: Shield },
      { key: "exportedToProd", label: "Hubs exported to Equip Dashboard", icon: Rocket },
    ],
  },
];
